import { memo, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { earningsRequest } from '../../lib/earningsClient.js'
import { earningsDateLabel, earningsLabel, nextEarnings } from '../../lib/chart/earningsDates.js'
import { EarningsPrimitive } from '../../lib/chart/EarningsPrimitive.js'

function EarningsLayer({ symbol, chart, series, timeframe, timezone, tradingCalendar }) {
  const [snapshot, setSnapshot] = useState({ symbol, status: 'loading', events: [] })
  const [now, setNow] = useState(() => new Date())
  const [selected, setSelected] = useState(null)
  const rootRef = useRef(null)
  const popupRef = useRef(null)
  const primitiveRef = useRef(null)
  const current = useMemo(() => snapshot.symbol === symbol ? snapshot : { status: 'loading', events: [] }, [snapshot, symbol])

  useEffect(() => {
    let disposed = false
    let loading = false
    const refresh = async () => {
      if (loading || document.visibilityState === 'hidden') return
      loading = true
      setNow(new Date())
      try {
        const result = await earningsRequest(symbol)
        if (!disposed) setSnapshot({ ...result, symbol, status: 'ready' })
      } catch {
        if (!disposed) setSnapshot({ symbol, status: 'error', events: [] })
      } finally { loading = false }
    }
    setSelected(null)
    refresh()
    const timer = setInterval(refresh, 60_000)
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      disposed = true
      clearInterval(timer)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [symbol])

  const next = useMemo(() => nextEarnings(current.events, now), [current.events, now])
  const labeledEvents = useMemo(() => current.events.map(event => ({ event, label: earningsLabel(event) })), [current.events])

  useLayoutEffect(() => {
    if (!chart || !series || current.status !== 'ready') return
    let lastInk = null
    const ink = () => {
      const element = rootRef.current
      if (element?.isConnected) lastInk = getComputedStyle(element).color
      return lastInk
    }
    const primitive = new EarningsPrimitive({ events: current.events, next, timeframe, timezone,
      tradingCalendar, onSelect: setSelected, ink })
    primitiveRef.current = primitive
    series.attachPrimitive(primitive)
    return () => {
      // Detach before the browser paints a symbol switch. A queued native
      // callback also tolerates a cleared DOM ref through the last-ink fallback.
      // LWC removes its chart element on disposal. Calling detachPrimitive on
      // that disposed chart schedules a redraw against destroyed canvases.
      try {
        if (chart.chartElement?.()?.isConnected !== false) series.detachPrimitive(primitive)
      } catch (error) {
        if (error.message !== 'Object is disposed') throw error
      } finally { primitive.detached(); primitiveRef.current = null }
    }
  }, [chart, series, current, timeframe, timezone, tradingCalendar, next])

  useEffect(() => {
    if (!selected) return
    const dismissOutside = event => {
      if (!popupRef.current?.contains(event.target)) setSelected(null)
    }
    const dismissEscape = event => {
      if (event.key === 'Escape') { event.stopPropagation(); setSelected(null) }
    }
    document.addEventListener('pointerdown', dismissOutside, true)
    document.addEventListener('keydown', dismissEscape, true)
    return () => {
      document.removeEventListener('pointerdown', dismissOutside, true)
      document.removeEventListener('keydown', dismissEscape, true)
    }
  }, [selected])

  // Follow the native icon only while its card is open. Reading the pane DOM
  // bounds also handles window scaling, left axes, and volume-pane resizing.
  useLayoutEffect(() => {
    if (!selected) return
    let frame
    const position = () => {
      const card = popupRef.current
      const root = rootRef.current
      const anchor = primitiveRef.current?.anchorFor(selected[0].date)
      if (card && root && anchor) {
        const bounds = root.getBoundingClientRect()
        const left = Math.max(8, Math.min(anchor.x - bounds.left - card.offsetWidth / 2, bounds.width - card.offsetWidth - 8))
        const top = Math.max(8, anchor.y - bounds.top - card.offsetHeight - 10)
        card.style.left = `${left}px`
        card.style.top = `${top}px`
        card.style.visibility = 'visible'
      } else if (card) card.style.visibility = 'hidden'
      frame = requestAnimationFrame(position)
    }
    position()
    return () => cancelAnimationFrame(frame)
  }, [selected])

  const selectedEvents = current.events.filter(event => selected?.some(item => item.date === event.date))
  const message = current.status === 'loading' ? 'Loading earnings date…'
    : current.status === 'error' ? 'Earnings date unavailable'
      : next ? `Earnings ${earningsLabel(next)}` : 'Next earnings date not supplied'
  return <div ref={rootRef} data-testid="earnings-layer" className="absolute inset-0 pointer-events-none text-inkMuted z-20">
    <span className="sr-only" role="status" data-testid="earnings-status">{message}</span>
    {chart && series && current.status === 'ready' && <div className="sr-only">
      {labeledEvents.map(({ event, label }) => <button key={event.date} type="button"
        aria-label={`Earnings ${label}`} onClick={() => setSelected([event])}>
        {label}
      </button>)}
    </div>}
    {selectedEvents.length > 0 && <div ref={popupRef}
      className="absolute rounded-lg border border-border bg-canvas p-3 text-xs text-ink pointer-events-auto shadow-lg overflow-auto"
      style={{ width: 240, maxWidth: 'calc(100% - 16px)', maxHeight: 'calc(100% - 16px)', visibility: 'hidden' }}
      role="dialog" aria-label="Earnings dates">
      <div className="flex items-center gap-2 border-b border-border pb-2 mb-3">
        <span className="rounded border border-inkMuted px-1 font-semibold" aria-hidden="true">E</span>
        <span className="font-semibold text-sm">Earnings</span>
        <span className="text-inkMuted">{symbol}</span>
        <button type="button" className="ml-auto text-inkMuted px-1" onClick={() => setSelected(null)} aria-label="Close earnings dates">×</button>
      </div>
      {selectedEvents.map(event => <dl key={event.date} className="space-y-2 mb-1">
        <div className="flex justify-between gap-3"><dt className="text-inkMuted">Date</dt><dd>{earningsDateLabel(event)}</dd></div>
        {event.session !== 'unknown' && <div className="flex justify-between gap-3"><dt className="text-inkMuted">Time</dt><dd>{event.session === 'premarket' ? 'Before open' : 'After close'}</dd></div>}
        {event.source === 'estimation' && <div className="text-right text-inkMuted">Estimated</div>}
      </dl>)}
    </div>}
  </div>
}

// Quote-driven parent renders do not change earnings metadata. Native primitive
// subscriptions still redraw on candle/viewport changes without React work.
export default memo(EarningsLayer)
